/**
 * PlaidLinkContext — Singleton Plaid Link SDK provider
 *
 * Ensures react-plaid-link only embeds the script once,
 * regardless of how many components need connect/reconnect.
 */

import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { usePlaidLink as usePlaidLinkSDK, PlaidLinkOnSuccessMetadata } from 'react-plaid-link';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase/config';

// ── Plaid error message mapping ──

function getPlaidErrorMessage(error: any): string {
  if (!error) return 'Bank connection was cancelled.';
  switch (error.error_code) {
    case 'INSTITUTION_NOT_FOUND':
      return "Your bank isn't available yet. You can enter your numbers manually.";
    case 'INSTITUTION_DOWN':
      return "Your bank's system is temporarily down. Try again in a few hours.";
    case 'INVALID_CREDENTIALS':
      return "Couldn't log in. Double-check your bank username and password.";
    case 'MFA_NOT_SUPPORTED':
      return 'There was an issue with verification. Try again or use a different account.';
    case 'INSTITUTION_NOT_RESPONDING':
      return "Your bank isn't responding. Try again later.";
    default:
      return error.display_message || 'Something went wrong connecting your bank. Try again.';
  }
}

interface PlaidConnectionResult {
  institutionName: string;
  transactionsSynced: number;
  accountMask: string | null;
}

interface PlaidLinkState {
  connect: () => Promise<void>;
  open: () => void;
  ready: boolean;
  loading: boolean;
  connected: boolean;
  error: string | null;
  institutionName: string | null;
  connectionResult: PlaidConnectionResult | null;
  reconnect: (itemId: string) => Promise<void>;
}

const PlaidLinkContext = createContext<PlaidLinkState | null>(null);

export function PlaidLinkProvider({ children }: { children: React.ReactNode }) {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [institutionName, setInstitutionName] = useState<string | null>(null);
  const [connectionResult, setConnectionResult] = useState<PlaidConnectionResult | null>(null);
  const shouldAutoOpen = useRef(false);
  const modeRef = useRef<'connect' | 'update'>('connect');
  const itemIdRef = useRef<string | null>(null);

  const onSuccess = useCallback(async (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
    setLoading(true);
    setError(null);
    try {
      if (modeRef.current === 'update') {
        const reauthFn = httpsCallable<{ itemId: string }, { success: boolean }>(
          functions, 'plaidCompleteReauth'
        );
        await reauthFn({ itemId: itemIdRef.current! });
        setConnected(true);
      } else {
        const exchangeFn = httpsCallable<
          { publicToken: string; institutionId: string | null; institutionName: string | null; selectedAccountIds: string[] },
          { success: boolean; itemId: string; institutionName: string; transactionsSynced: number }
        >(functions, 'plaidExchangeToken');

        const result = await exchangeFn({
          publicToken,
          institutionId: metadata.institution?.institution_id || null,
          institutionName: metadata.institution?.name || null,
          selectedAccountIds: metadata.accounts?.map((a: any) => a.id) || [],
        });

        setInstitutionName(result.data.institutionName);
        setConnectionResult({
          institutionName: result.data.institutionName,
          transactionsSynced: result.data.transactionsSynced,
          accountMask: metadata.accounts?.[0]?.mask || null,
        });
        setConnected(true);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save bank connection.');
    } finally {
      setLoading(false);
      modeRef.current = 'connect';
      itemIdRef.current = null;
    }
  }, []);

  const { open, ready } = usePlaidLinkSDK({
    token: linkToken,
    onSuccess,
    onExit: (err) => {
      if (err) {
        setError(getPlaidErrorMessage(err));
      }
      setLoading(false);
      modeRef.current = 'connect';
      itemIdRef.current = null;
    },
  });

  useEffect(() => {
    if (shouldAutoOpen.current && ready) {
      shouldAutoOpen.current = false;
      open();
    }
  }, [ready, open]);

  const connect = useCallback(async () => {
    setLoading(true);
    setError(null);
    modeRef.current = 'connect';
    try {
      const createTokenFn = httpsCallable<void, { linkToken: string }>(
        functions, 'plaidCreateLinkToken'
      );
      const result = await createTokenFn();
      setLinkToken(result.data.linkToken);
      shouldAutoOpen.current = true;
    } catch (err: any) {
      setError(err.message || 'Could not initialize bank connection.');
      setLoading(false);
    }
  }, []);

  const reconnect = useCallback(async (itemId: string) => {
    setLoading(true);
    setError(null);
    modeRef.current = 'update';
    itemIdRef.current = itemId;
    try {
      const createTokenFn = httpsCallable<
        { mode: string; itemId: string },
        { linkToken: string }
      >(functions, 'plaidCreateLinkToken');
      const result = await createTokenFn({ mode: 'update', itemId });
      setLinkToken(result.data.linkToken);
      shouldAutoOpen.current = true;
    } catch (err: any) {
      setError(err.message || 'Could not initialize re-authentication.');
      setLoading(false);
      modeRef.current = 'connect';
      itemIdRef.current = null;
    }
  }, []);

  const value: PlaidLinkState = {
    connect,
    open: open as () => void,
    ready,
    loading,
    connected,
    error,
    institutionName,
    connectionResult,
    reconnect,
  };

  return (
    <PlaidLinkContext.Provider value={value}>
      {children}
    </PlaidLinkContext.Provider>
  );
}

export function usePlaidLink(): PlaidLinkState {
  const ctx = useContext(PlaidLinkContext);
  if (!ctx) throw new Error('usePlaidLink must be used within PlaidLinkProvider');
  return ctx;
}
